import { useState, useEffect, useCallback } from 'react';

// Global cache for currency rate to prevent duplicate API calls
let globalUSDRate: number | null = null;
let globalLastFetch: number = 0;
let globalLoading: boolean = false;
let globalError: string | null = null;
const CACHE_DURATION = 30000; // 30 seconds

export const useCurrencyRate = () => {
  const [usdRate, setUsdRate] = useState<number>(globalUSDRate || 40); // Default fallback rate
  const [loading, setLoading] = useState<boolean>(globalLoading);
  const [error, setError] = useState<string | null>(globalError);

  // Fetch current USD rate from API
  const fetchUSDRate = useCallback(async () => {
    const now = Date.now();
    
    // Check if we have a recent cached value
    if (globalUSDRate && (now - globalLastFetch < CACHE_DURATION)) {
      console.log(`[useCurrencyRate] Using cached USD rate: ${globalUSDRate}`);
      if (usdRate !== globalUSDRate) {
        setUsdRate(globalUSDRate);
      }
      return globalUSDRate;
    }

    // Prevent multiple simultaneous requests
    if (globalLoading) {
      console.log('[useCurrencyRate] Request already in progress, waiting...');
      return globalUSDRate || 40;
    }

    try {
      globalLoading = true;
      setLoading(true);
      setError(null);
      globalError = null;
      
      console.log('[useCurrencyRate] Fetching fresh USD rate from API');
      const response = await fetch('/api/v1/currency/usd-rate');
      if (response.ok) {
        const data = await response.json();
        const rate = data.usd_rate || 40;
        
        // Update global cache
        globalUSDRate = rate;
        globalLastFetch = now;
        
        // Update local state
        setUsdRate(rate);
        console.log(`[useCurrencyRate] Updated USD rate to ${rate}`);
        return rate;
      } else {
        throw new Error(`Failed to fetch USD rate: ${response.status}`);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch USD rate';
      globalError = errorMessage;
      setError(errorMessage);
      console.error('[useCurrencyRate] Error:', error);
      return 40; // Return fallback rate
    } finally {
      globalLoading = false;
      setLoading(false);
    }
  }, [usdRate]);

  // Initial fetch - only if no cached data
  useEffect(() => {
    if (!globalUSDRate || (Date.now() - globalLastFetch > CACHE_DURATION)) {
      fetchUSDRate();
    } else {
      // Use cached data
      setUsdRate(globalUSDRate);
      setLoading(globalLoading);
      setError(globalError);
    }
  }, [fetchUSDRate]);

  // Convert USD to UAH
  const convertUSDToUAH = (usdAmount: number): number => {
    return usdAmount * usdRate;
  };

  // Convert UAH to USD
  const convertUAHToUSD = (uahAmount: number): number => {
    return uahAmount / usdRate;
  };

  return {
    usdRate,
    loading,
    error,
    fetchUSDRate,
    convertUSDToUAH,
    convertUAHToUSD,
    refetchRate: fetchUSDRate
  };
};
